import React, { useState } from 'react';
import { Form, Button, Modal, ListGroup } from 'react-bootstrap';
import Flags from "react-world-flags";
import { FaTrashAlt } from "react-icons/fa";
import GeneralSettingsService from '../../services/GeneralSettingsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CreateForm = ({ userName, onCancel, onDataReload }) => {
    const [formValidated, setFormValidated] = useState(false);
    const [paramRates, setParamRates] = useState([]);
    const [emailInterac, setEmailInterac] = useState('');
    const [emailNotification, setEmailNotification] = useState('');
    const [bankDepositMinAmount, setBankDepositMinAmount] = useState('');
    const [bankDepositMaxAmount, setBankDepositMaxAmount] = useState('');
    const [walletDepositMinAmount, setWalletDepositMinAmount] = useState('');
    const [walletDepositMaxAmount, setWalletDepositMaxAmount] = useState('');
    const [interacName, setInteracName] = useState('');
    const [securityQuestion, setSecurityQuestion] = useState('');
    const [securityAnswer, setSecurityAnswer] = useState('');

    // États pour suivre la validation des champs
    const [emailInteracTouched, setEmailInteracTouched] = useState(false);
    const [emailNotificationTouched, setEmailNotificationTouched] = useState(false);
    const [paramRatesTouched, setParamRatesTouched] = useState([]);

    // Ajout de WAVE comme méthode de retrait
    const withdrawalMethods = ["BANK", "ORANGE_MONEY", "MTN_MOBILE_MONEY", "WAVE"];

    // Ajout du Sénégal comme pays
    const countries = [
        { code: "CM", name: "Cameroun" },
        { code: "CI", name: "Côte d'Ivoire" },
        { code: "BJ", name: "Bénin" },
        { code: "BF", name: "Burkina Faso" },
        { code: "SN", name: "Sénégal" }
    ];

    // Constante pour la limite maximale de paramètres de taux
    const MAX_PARAM_RATES = 5;

    // Fonctions de validation
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidNumber = (value) => {
        return value !== '' && !isNaN(value) && Number(value) > 0;
    };

    // Fonction pour obtenir les pays disponibles pour un paramètre de taux spécifique
    const getAvailableCountries = (index) => {
        const selectedCountriesInOtherRates = paramRates
            .filter((pr, prIndex) => prIndex !== index && pr.country !== '')
            .map(pr => pr.country);
        return countries.filter(country => !selectedCountriesInOtherRates.includes(country.name));
    };

    // Calcul des pays sélectionnés et pays disponibles
    const selectedCountries = paramRates.filter(pr => pr.country !== '').map(pr => pr.country);
    const availableCountries = countries.filter(country => !selectedCountries.includes(country.name));

    const handleAddParamRate = () => {
        if (availableCountries.length > 0 && paramRates.length < MAX_PARAM_RATES) {
            const newParamRates = [...paramRates, {
                country: '',
                rate: '',
                withDrawalMethods: [],
                userName: userName,
            }];
            setParamRates(newParamRates);

            // Mettre à jour les états de validation pour le nouveau paramètre
            setParamRatesTouched([...paramRatesTouched, {
                countryTouched: false,
                rateTouched: false,
                methodsTouched: false
            }]);
        } else if (paramRates.length >= MAX_PARAM_RATES) {
            toast.warning(`Vous ne pouvez pas ajouter plus de ${MAX_PARAM_RATES} paramètres de taux.`);
        } else {
            toast.warning('Aucun pays disponible pour un nouveau paramètre de taux.');
        }
    };

    const handleParamRateChange = (index, field, value) => {
        const updatedParamRates = [...paramRates];
        updatedParamRates[index][field] = value;
        setParamRates(updatedParamRates);

        // Mettre à jour l'état de validation du champ
        const updatedParamRatesTouched = [...paramRatesTouched];
        if (field === 'country') updatedParamRatesTouched[index].countryTouched = true;
        if (field === 'rate') updatedParamRatesTouched[index].rateTouched = true;
        setParamRatesTouched(updatedParamRatesTouched);
    };

    const handleWithdrawalMethodSelect = (index, e) => {
        const method = e.target.value;
        if (method && !paramRates[index].withDrawalMethods.includes(method)) {
            const updatedParamRates = [...paramRates];
            updatedParamRates[index].withDrawalMethods.push(method);
            setParamRates(updatedParamRates);

            // Mettre à jour l'état de validation des méthodes
            const updatedParamRatesTouched = [...paramRatesTouched];
            updatedParamRatesTouched[index].methodsTouched = true;
            setParamRatesTouched(updatedParamRatesTouched);

            e.target.value = '';
        }
    };

    const handleWithdrawalMethodRemove = (index, method) => {
        const updatedParamRates = [...paramRates];
        updatedParamRates[index].withDrawalMethods = updatedParamRates[index].withDrawalMethods.filter(m => m !== method);
        setParamRates(updatedParamRates);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Marquer tous les champs comme touchés
        setEmailInteracTouched(true);
        setEmailNotificationTouched(true);

        // Validation des champs principaux
        const isEmailInteracValid = isValidEmail(emailInterac);
        const isEmailNotificationValid = isValidEmail(emailNotification);
        const isBankDepositMinAmountValid = isValidNumber(bankDepositMinAmount);
        const isBankDepositMaxAmountValid = isValidNumber(bankDepositMaxAmount);
        const isWalletDepositMinAmountValid = isValidNumber(walletDepositMinAmount);
        const isWalletDepositMaxAmountValid = isValidNumber(walletDepositMaxAmount);
        const isInteracNameValid = interacName.trim() !== '';
        const isSecurityQuestionValid = securityQuestion.trim() !== '';
        const isSecurityAnswerValid = securityAnswer.trim() !== '';

        // Validation des paramètres de taux
        const areParamRatesValid = paramRates.length > 0 && paramRates.every((paramRate, index) =>
            paramRate.country !== '' &&
            paramRate.rate !== '' &&
            paramRate.withDrawalMethods.length > 0
        );

        // Vérification globale de la validation
        if (!isEmailInteracValid || !isEmailNotificationValid ||
            !isBankDepositMinAmountValid || !isBankDepositMaxAmountValid ||
            !isWalletDepositMinAmountValid || !isWalletDepositMaxAmountValid ||
            !isInteracNameValid || !isSecurityQuestionValid || !isSecurityAnswerValid ||
            !areParamRatesValid) {
            toast.error('Veuillez remplir correctement tous les champs requis.');
            return;
        }

        const formData = {
            emailInterac,
            emailNotification,
            bankDepositMinAmount: Number(bankDepositMinAmount),
            bankDepositMaxAmount: Number(bankDepositMaxAmount),
            walletDepositMinAmount: Number(walletDepositMinAmount),
            walletDepositMaxAmount: Number(walletDepositMaxAmount),
            interacName,
            securityQuestion,
            securityAnswer,
            userName,
            paramRates: paramRates.map(paramRate => ({
                ...paramRate,
                withDrawalMethods: paramRate.withDrawalMethods.join(', ')
            }))
        };

        try {
            await GeneralSettingsService.createParameter(formData);
            toast.success('Paramètres enregistrés avec succès!');
            onCancel();
            onDataReload();
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement des paramètres.');
        }
    };

    return (
        <Form noValidate validated={formValidated} onSubmit={handleSubmit}>
            <Modal.Body>
                <Form.Group controlId="emailInterac" className="form-floating mt-3">
                    <Form.Control
                        type="email"
                        value={emailInterac}
                        onChange={(e) => {
                            setEmailInterac(e.target.value);
                            setEmailInteracTouched(true);
                        }}
                        required
                        className={
                            emailInteracTouched
                                ? (isValidEmail(emailInterac) ? 'is-valid' : 'is-invalid')
                                : ''
                        }
                    />
                    <Form.Label>Email Interac</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un email valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="emailNotification" className="form-floating mt-3">
                    <Form.Control
                        type="email"
                        value={emailNotification}
                        onChange={(e) => {
                            setEmailNotification(e.target.value);
                            setEmailNotificationTouched(true);
                        }}
                        required
                        className={
                            emailNotificationTouched
                                ? (isValidEmail(emailNotification) ? 'is-valid' : 'is-invalid')
                                : ''
                        }
                    />
                    <Form.Label>Email Notification</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un email valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="bankDepositMinAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={bankDepositMinAmount}
                        onChange={(e) => setBankDepositMinAmount(e.target.value)}
                        required
                        className={bankDepositMinAmount !== '' ? (isValidNumber(bankDepositMinAmount) ? 'is-valid' : 'is-invalid') : ''}
                    />
                    <Form.Label>Montant Minimum Dépôt Bancaire</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un montant valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="bankDepositMaxAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={bankDepositMaxAmount}
                        onChange={(e) => setBankDepositMaxAmount(e.target.value)}
                        required
                        className={bankDepositMaxAmount !== '' ? (isValidNumber(bankDepositMaxAmount) ? 'is-valid' : 'is-invalid') : ''}
                    />
                    <Form.Label>Montant Maximum Dépôt Bancaire</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un montant valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="walletDepositMinAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={walletDepositMinAmount}
                        onChange={(e) => setWalletDepositMinAmount(e.target.value)}
                        required
                        className={walletDepositMinAmount !== '' ? (isValidNumber(walletDepositMinAmount) ? 'is-valid' : 'is-invalid') : ''}
                    />
                    <Form.Label>Montant Minimum Dépôt Portefeuille</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un montant valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="walletDepositMaxAmount" className="form-floating mt-3">
                    <Form.Control
                        type="number"
                        value={walletDepositMaxAmount}
                        onChange={(e) => setWalletDepositMaxAmount(e.target.value)}
                        required
                        className={walletDepositMaxAmount !== '' ? (isValidNumber(walletDepositMaxAmount) ? 'is-valid' : 'is-invalid') : ''}
                    />
                    <Form.Label>Montant Maximum Dépôt Portefeuille</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un montant valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="interacName" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={interacName}
                        onChange={(e) => setInteracName(e.target.value)}
                        required
                        className={interacName !== '' ? 'is-valid' : 'is-invalid'}
                    />
                    <Form.Label>Nom Interac</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer un nom valide.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="securityQuestion" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={securityQuestion}
                        onChange={(e) => setSecurityQuestion(e.target.value)}
                        required
                        className={securityQuestion !== '' ? 'is-valid' : 'is-invalid'}
                    />
                    <Form.Label>Question de Sécurité</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer une question de sécurité.
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="securityAnswer" className="form-floating mt-3">
                    <Form.Control
                        type="text"
                        value={securityAnswer}
                        onChange={(e) => setSecurityAnswer(e.target.value)}
                        required
                        className={securityAnswer !== '' ? 'is-valid' : 'is-invalid'}
                    />
                    <Form.Label>Réponse de Sécurité</Form.Label>
                    <Form.Control.Feedback type="invalid">
                        Veuillez entrer une réponse de sécurité.
                    </Form.Control.Feedback>
                </Form.Group>

                <div className="mt-4 mb-4 d-flex align-items-center">
                    <div className="flex-grow-1 border-bottom"></div>
                    <h5 className="mx-3 mb-0">Paramètres de Taux</h5>
                    <div className="flex-grow-1 border-bottom"></div>
                </div>

                {paramRates.map((paramRate, index) => {
                    const availableCountriesForRate = getAvailableCountries(index);
                    const selectedCountry = availableCountriesForRate.find(country => country.name === paramRate.country);

                    // Créer l'état de validation s'il n'existe pas
                    if (!paramRatesTouched[index]) {
                        paramRatesTouched[index] = {
                            countryTouched: false,
                            rateTouched: false,
                            methodsTouched: false
                        };
                    }

                    return (
                        <div key={index} className="mt-3 border p-3 rounded">
                            <Form.Group controlId={`country-${index}`} className="form-floating">
                                <Form.Select
                                    value={paramRate.country}
                                    onChange={(e) => handleParamRateChange(index, 'country', e.target.value)}
                                    required
                                    className={
                                        paramRatesTouched[index]?.countryTouched
                                            ? (paramRate.country ? 'is-valid' : 'is-invalid')
                                            : ''
                                    }
                                >
                                    <option value="">Sélectionnez un pays</option>
                                    {availableCountriesForRate.map((country, i) => (
                                        <option key={i} value={country.name}>
                                            {country.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Label>
                                    {selectedCountry ? (
                                        <div className="d-flex align-items-center">
                                            <Flags
                                                code={selectedCountry.code}
                                                style={{ width: "20px", marginRight: "8px" }}
                                            />
                                            Pays
                                        </div>
                                    ) : 'Pays'}
                                </Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    Veuillez sélectionner un pays.
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`rate-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="number"
                                    value={paramRate.rate}
                                    onChange={(e) => handleParamRateChange(index, 'rate', e.target.value)}
                                    required
                                    className={
                                        paramRatesTouched[index]?.rateTouched
                                            ? (paramRate.rate ? 'is-valid' : 'is-invalid')
                                            : ''
                                    }
                                />
                                <Form.Label>Taux</Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    Veuillez entrer un taux valide.
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`withDrawalMethods-${index}`} className="mt-3">
                                <Form.Label>Méthodes de Retrait</Form.Label>
                                <Form.Select
                                    onChange={(e) => handleWithdrawalMethodSelect(index, e)}
                                    value=""
                                >
                                    <option value="">Sélectionner une méthode</option>
                                    {withdrawalMethods
                                        .filter(method => !paramRate.withDrawalMethods.includes(method))
                                        .map((method, i) => (
                                            <option key={i} value={method}>
                                                {method}
                                            </option>
                                        ))
                                    }
                                </Form.Select>

                                <ListGroup className="mt-2">
                                    {paramRate.withDrawalMethods.map((method, i) => (
                                        <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center">
                                            {method}
                                            <Button
                                                variant="link"
                                                className="p-0 text-danger"
                                                onClick={() => handleWithdrawalMethodRemove(index, method)}
                                            >
                                                <FaTrashAlt />
                                            </Button>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                                {paramRatesTouched[index]?.methodsTouched && paramRate.withDrawalMethods.length === 0 && (
                                    <div className="text-danger small">
                                        Veuillez sélectionner au moins une méthode de retrait.
                                    </div>
                                )}
                            </Form.Group>
                        </div>
                    );
                })}

                {/* Afficher le bouton seulement si on n'a pas atteint la limite de 5 */}
                {paramRates.length < MAX_PARAM_RATES && (
                    <div className="mt-3 d-flex">
                        <Button
                            variant="primary"
                            onClick={handleAddParamRate}
                            disabled={availableCountries.length === 0}
                        >
                            Ajouter Paramètres de Taux
                        </Button>
                    </div>
                )}

                {/* Message informatif lorsque la limite est atteinte */}
                {paramRates.length >= MAX_PARAM_RATES && (
                    <div className="mt-3 alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        Vous avez atteint le nombre maximum de paramètres de taux ({MAX_PARAM_RATES}).
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-end gap-2" style={{ paddingBottom: '10px', paddingTop: '15px' }}>
                <Button variant="secondary" onClick={onCancel}>
                    Annuler
                </Button>
                <Button variant="primary" type="submit">
                    Sauvegarder
                </Button>
            </Modal.Footer>
            <ToastContainer />
        </Form>
    );
};

export default CreateForm;